import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { API } from "../App";
import "../components/sojourns/sojourns.css";
import JsonLd from "../components/JsonLd";
import { useCountdown, useSojournsChrome } from "../components/sojourns/primitives";
import { NoteHeader, Faq, faqJsonLd, NotesList } from "../components/sojourns/NoteChrome";
import { FollowLink, Follow } from "../components/sojourns/Follow";
import { SOJOURNS_FAQ, SOJOURNS_ORIGIN, SOJOURNS_URL } from "../content/sojournsNotes";

const FONTS = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Mulish:wght@400;600&family=Space+Mono&family=Cinzel&display=swap";
const URL = `${SOJOURNS_URL}/notes`;
const DESC = "Six short notes on the thinking behind Savi Sojourns, a month-long mentored retreat for business owners, and plain answers to the questions people ask before they apply.";
const FAQ_LD = faqJsonLd(SOJOURNS_FAQ);
const CRUMBS_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Savi Coliving", item: `${SOJOURNS_ORIGIN}/` },
    { "@type": "ListItem", position: 2, name: "Savi Sojourns", item: SOJOURNS_URL },
    { "@type": "ListItem", position: 3, name: "Notes", item: URL },
  ],
};

// /sojourns/notes: the reading list and the questions, kept off the main page behind one discreet link.
export default function SojournsNotesPage() {
  const [settings, setSettings] = useState({ reveal_date: null, edition: "001" });
  const days = useCountdown(settings.reveal_date);
  const open = days !== null && days <= 0;
  useSojournsChrome();

  useEffect(() => {
    axios.get(`${API}/sojourns/settings`).then((r) => setSettings(r.data || {})).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="sj arrived skip" data-testid="sojourns-notes-page">
      <Helmet title="Notes · Savi Sojourns, a month-long retreat for business owners">
        <meta name="description" content={DESC} />
        <link rel="canonical" href={URL} />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Savi Sojourns" />
        <meta property="og:title" content="Notes · Savi Sojourns" />
        <meta property="og:description" content={DESC} />
        <meta property="og:url" content={URL} />
        <meta property="og:image" content={`${SOJOURNS_ORIGIN}/sojourn-media/og.jpg?v=4`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="theme-color" content="#12100E" />
        <link rel="icon" href="/sojourn-media/sojourns-favicon.ico?v=1" sizes="16x16 32x32 48x48" />
        <link rel="apple-touch-icon" sizes="180x180" href="/sojourn-media/sojourns-apple-touch-icon.png?v=1" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href={FONTS} />
      </Helmet>
      <JsonLd id="sojourns-notes-faq" data={FAQ_LD} />
      <JsonLd id="sojourns-notes-crumbs" data={CRUMBS_LD} />

      <NoteHeader open={open} />

      <main className="sj-note">
        <div className="sj-col wide">
          <header className="sj-note-head">
            <p className="sj-label">Savi Sojourns · Notes</p>
            <h1 className="sj-note-title" data-testid="sojourns-notes-title">The thinking behind the month.</h1>
            <p className="sj-note-lede">Six short notes on why Sojourns is a month and not a weekend, why it is alcohol-free, why the business keeps running, and where it sits against a mastermind or a coach.</p>
          </header>
          <NotesList testid="sojourns-notes" />

          <h2 className="sj-note-h2" style={{ marginTop: "var(--sj-gap)" }}>Questions, answered plainly</h2>
          <Faq items={SOJOURNS_FAQ} />

          <div className="sj-note-cta" data-testid="sojourns-notes-cta">
            <p className="sj-label">Sojourn No. {settings.edition || "001"}</p>
            <p>{open ? "The gate is open. Twelve places, by application." : "Applications open on 24 November 2026, with the full moon. Register and you'll hear first."}</p>
            <Link to="/sojourns#register" className="sj-btn link" data-testid="sojourns-notes-register">{open ? "Apply now" : "Register interest"}</Link>
            <Follow quiet line="Most of what there is to know will appear on Instagram first. Keep an eye on it." testid="sojourns-notes-follow" />
          </div>
          <p className="sj-contact">
            Steve Haynes, Savi · <a href="mailto:hola@savicoliving.com">hola@savicoliving.com</a> · <FollowLink testid="sojourns-notes-instagram" /> · <Link to="/sojourns">Savi Sojourns</Link> · <a href="https://www.savicoliving.com">savicoliving.com</a>
          </p>
        </div>
      </main>
    </div>
  );
}
